/**
 * Cross-Framework Map Page (Enhanced)
 * Shows where evidence is used across different frameworks
 * "Upload Once, Use Everywhere" philosophy
 * 
 * Superpowers:
 * #1: Cross-Framework Intelligence - Evidence reuse visualization
 * #2: Ownership & Accountability - Mapping ownership chain
 * #3: Audit Defensibility - Mapping confidence and timestamps
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Network, 
  FileText, 
  CheckCircle2, 
  AlertTriangle,
  ArrowRight,
  Search,
  Filter,
  Eye,
  Link2,
  Zap,
  Grid3X3,
  List,
  TrendingUp,
  Download,
  HelpCircle,
  ArrowLeft,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { API } from '../../App';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { useToast } from '../../hooks/use-toast';

// ============================================================================
// CONSTANTS
// ============================================================================

const FRAMEWORKS = ['All', 'CSRD', 'CDP', 'TCFD', 'CSDDD', 'GRI', 'SBTi', 'EU_TAXONOMY'];

const STATUS_CONFIG = {
  mapped: { label: 'Mapped', bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2 },
  suggested: { label: 'AI Suggested', bg: 'bg-purple-500/20', text: 'text-purple-400', icon: Zap },
  gap: { label: 'Gap', bg: 'bg-red-500/20', text: 'text-red-400', icon: AlertTriangle },
};

// ============================================================================
// STATS BAR COMPONENT
// ============================================================================

function StatsBar({ stats, loading }) {
  if (loading) {
    return (
      <div className="grid grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 animate-pulse">
            <div className="h-8 bg-[#2A2A2A] rounded mb-2" />
            <div className="h-4 bg-[#2A2A2A] rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  const avgReuse = stats?.averageFrameworksPerEvidence || 0;
  const efficiencyGain = Math.round((avgReuse - 1) * 100);

  return (
    <div className="grid grid-cols-4 gap-4">
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/20">
            <FileText size={18} className="text-emerald-400" />
          </div>
          <div>
            <div className="text-2xl font-bold text-white">{stats?.totalEvidence || 0}</div>
            <div className="text-xs text-[#6B6B6B]">Evidence Items</div>
          </div>
        </div>
      </div>
      
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/20">
            <Network size={18} className="text-blue-400" />
          </div>
          <div>
            <div className="text-2xl font-bold text-white">{stats?.totalMappings || 0}</div>
            <div className="text-xs text-[#6B6B6B]">Total Mappings</div>
          </div>
        </div>
      </div>
      
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 ring-1 ring-teal-500/30">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-teal-500/20">
            <TrendingUp size={18} className="text-teal-400" />
          </div>
          <div>
            <div className="text-2xl font-bold text-white">{avgReuse}x</div>
            <div className="text-xs text-[#6B6B6B]">Avg Reuse</div>
          </div>
        </div>
      </div>
      
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 ring-1 ring-emerald-500/30">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/20">
            <Zap size={18} className="text-emerald-400" />
          </div>
          <div>
            <div className="text-2xl font-bold text-emerald-400">{efficiencyGain > 0 ? `+${efficiencyGain}%` : '0%'}</div>
            <div className="text-xs text-[#6B6B6B]">Efficiency Gain</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// TOP REUSED EVIDENCE CALLOUT
// ============================================================================

function TopReusedCallout({ topReused }) {
  if (!topReused) return null;
  
  return (
    <div className="bg-gradient-to-r from-teal-500/10 via-emerald-500/10 to-cyan-500/10 border border-teal-500/30 rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-teal-500/20 flex items-center justify-center shrink-0">
          <Zap size={20} className="text-teal-400" />
        </div>
        <div className="flex-1">
          <p className="text-xs font-medium text-teal-300 mb-0.5">Most Reused Evidence</p>
          <p className="text-sm font-semibold text-white">
            {topReused.fileName}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            Used in <span className="text-teal-400 font-medium">{topReused.frameworkCount} frameworks</span>, 
            satisfying <span className="text-teal-400 font-medium">{topReused.requirementCount} requirements</span>
          </p>
        </div>
        <Badge className="bg-teal-500/20 text-teal-300 border-teal-500/30 shrink-0">
          {topReused.frameworkCount}x reuse
        </Badge>
      </div>
    </div>
  );
}

// ============================================================================
// EVIDENCE MAPPING CARD
// ============================================================================

function EvidenceMappingCard({ evidence, onOpenDetail }) {
  const [expanded, setExpanded] = useState(false);
  
  const mappings = evidence.framework_mappings || [];
  const uniqueFrameworks = [...new Set(mappings.map(m => m.framework_code).filter(Boolean))];
  const coverage = mappings.length > 0 
    ? Math.round((mappings.filter(m => m.mapping_confidence >= 0.7).length / mappings.length) * 100)
    : 0;
  
  return (
    <div 
      className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden hover:border-emerald-500/30 transition-all"
      data-testid={`mapping-card-${evidence.id}`}
    >
      {/* Header */}
      <div 
        className="p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-[#1F1F1F]">
              <FileText size={18} className="text-emerald-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">{evidence.original_filename}</h3>
              <p className="text-xs text-[#6B6B6B] mt-0.5">
                {evidence.evidence_type?.toUpperCase() || 'FILE'} • {uniqueFrameworks.length} frameworks
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#6B6B6B]">Coverage</span>
              <span className={`text-lg font-bold ${coverage >= 80 ? 'text-emerald-400' : coverage >= 60 ? 'text-amber-400' : 'text-red-400'}`}>
                {coverage}%
              </span>
            </div>
          </div>
        </div>
        
        {/* Framework Pills Preview */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {uniqueFrameworks.slice(0, 5).map((fw, idx) => {
            const fwMappings = mappings.filter(m => m.framework_code === fw);
            const avgConfidence = fwMappings.reduce((sum, m) => sum + (m.mapping_confidence || 0), 0) / fwMappings.length;
            const statusConfig = avgConfidence >= 0.7 ? STATUS_CONFIG.mapped : STATUS_CONFIG.suggested;
            
            return (
              <span 
                key={idx} 
                className={`px-2 py-1 text-[10px] font-medium rounded ${statusConfig.bg} ${statusConfig.text}`}
              >
                {fw} ({fwMappings.length})
              </span>
            );
          })}
          {uniqueFrameworks.length > 5 && (
            <span className="px-2 py-1 text-[10px] text-gray-500">
              +{uniqueFrameworks.length - 5} more
            </span>
          )}
          {uniqueFrameworks.length === 0 && (
            <span className="px-2 py-1 text-[10px] text-gray-500 italic">
              No framework mappings yet
            </span>
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-[#1F1F1F] p-4 bg-[#0A0A0A]">
          <h4 className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-3">Framework Mappings</h4>
          <div className="space-y-2">
            {mappings.length > 0 ? (
              mappings.slice(0, 10).map((mapping, idx) => {
                const statusConfig = mapping.mapping_confidence >= 0.7 ? STATUS_CONFIG.mapped : STATUS_CONFIG.suggested;
                const StatusIcon = statusConfig.icon;
                
                return (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-[#111111] rounded-lg">
                    <StatusIcon size={14} className={statusConfig.text} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white">{mapping.framework_code}</span>
                        <ArrowRight size={12} className="text-[#4A4A4A]" />
                        <span className="text-sm text-[#A0A0A0] truncate">
                          {mapping.requirement_code || mapping.requirement_title || 'General mapping'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`text-xs font-medium ${statusConfig.text}`}>{statusConfig.label}</span>
                      <span className="text-[10px] text-[#6B6B6B] block">
                        {Math.round((mapping.mapping_confidence || 0) * 100)}% confidence
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-3 bg-[#111111] rounded-lg text-center">
                <p className="text-sm text-gray-500">No mappings yet. Add framework mappings to maximize evidence reuse.</p>
              </div>
            )}
            
            {mappings.length > 10 && (
              <p className="text-xs text-gray-500 text-center py-2">
                +{mappings.length - 10} more mappings
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-2 mt-4">
            <button 
              onClick={() => onOpenDetail(evidence.id)}
              className="flex-1 py-2 bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#A0A0A0] text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1"
            >
              <Eye size={12} /> View Evidence
            </button>
            <Link
              to={`/evidence?selected=${evidence.id}`}
              className="flex-1 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1"
            >
              <Link2 size={12} /> Add Mapping
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MATRIX VIEW
// ============================================================================

function MatrixView({ evidence, frameworks }) {
  const uniqueFrameworks = [...new Set(
    evidence.flatMap(e => 
      (e.framework_mappings || []).map(m => m.framework_code).filter(Boolean)
    )
  )].sort();

  return (
    <Card className="bg-[#111111] border-[#1F1F1F]">
      <CardHeader>
        <CardTitle className="text-base text-white flex items-center justify-between">
          <span>Evidence × Framework Matrix</span>
          <div className="flex items-center gap-2 text-sm font-normal">
            <Badge variant="outline" className="gap-1 border-emerald-500/30 text-emerald-400">
              <CheckCircle2 className="h-3 w-3" />
              Mapped
            </Badge>
            <Badge variant="outline" className="gap-1 border-gray-600 text-gray-400">
              ○ Not Mapped
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-[#2A2A2A]">
                <th className="sticky left-0 z-10 bg-[#111111] p-3 text-left text-xs font-semibold text-gray-400 min-w-[200px] border-r border-[#1F1F1F]">
                  Evidence
                </th>
                {uniqueFrameworks.map(fw => (
                  <th
                    key={fw}
                    className="p-3 text-center text-xs font-semibold text-gray-400 min-w-[80px] border-l border-[#1F1F1F]"
                  >
                    {fw}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {evidence.slice(0, 15).map((ev, idx) => {
                const evMappings = ev.framework_mappings || [];
                const mappedFrameworks = new Set(evMappings.map(m => m.framework_code));
                
                return (
                  <tr 
                    key={ev.id}
                    className={`border-b border-[#1F1F1F] hover:bg-[#1A1A1A] transition-colors ${
                      idx % 2 === 0 ? 'bg-[#0D0D0D]' : 'bg-[#111111]'
                    }`}
                  >
                    <td className="sticky left-0 z-10 bg-inherit p-3 text-sm font-medium text-gray-300 border-r border-[#1F1F1F]">
                      <span className="truncate block max-w-[180px]" title={ev.original_filename}>
                        {ev.original_filename}
                      </span>
                    </td>
                    {uniqueFrameworks.map(fw => {
                      const isMapped = mappedFrameworks.has(fw);
                      const fwMappings = evMappings.filter(m => m.framework_code === fw);
                      
                      return (
                        <td
                          key={fw}
                          className="p-3 text-center border-l border-[#1F1F1F]"
                        >
                          {isMapped ? (
                            <div className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-emerald-500/20" title={`${fwMappings.length} mappings`}>
                              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                            </div>
                          ) : (
                            <div className="inline-flex items-center justify-center h-7 w-7">
                              <span className="h-4 w-4 rounded-full border-2 border-[#2A2A2A]" />
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {evidence.length > 15 && (
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500 mb-2">
              Showing 15 of {evidence.length} evidence items
            </p>
          </div>
        )}
        
        {uniqueFrameworks.length === 0 && (
          <div className="text-center py-8">
            <Network className="h-12 w-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500">No framework mappings yet</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// FRAMEWORK BREAKDOWN
// ============================================================================

function FrameworkBreakdown({ byFramework }) {
  if (!byFramework || byFramework.length === 0) {
    return (
      <Card className="bg-[#111111] border-[#1F1F1F]">
        <CardContent className="py-8 text-center">
          <Network className="h-12 w-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500">No framework mappings yet</p>
        </CardContent>
      </Card>
    );
  }

  const maxCount = Math.max(...byFramework.map(f => f.count));

  return (
    <Card className="bg-[#111111] border-[#1F1F1F]">
      <CardHeader>
        <CardTitle className="text-base text-white">Evidence by Framework</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {byFramework.map(fw => (
          <div key={fw.framework} className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-300">{fw.framework}</span>
              <span className="text-sm text-emerald-400">{fw.count} evidence items</span>
            </div>
            <Progress 
              value={(fw.count / maxCount) * 100} 
              className="h-2 bg-[#2A2A2A]"
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export default function CrossFrameworkMapPage() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFramework, setSelectedFramework] = useState('All');
  const [activeView, setActiveView] = useState('list');
  const [evidence, setEvidence] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  // Fetch data from evidence API + CFI intelligence endpoints
  const fetchData = useCallback(async () => {
    setLoading(true);
    setStatsLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Fetch evidence map, evidence stats, and CFI stats in parallel
      const [mapRes, statsRes, cfiRes] = await Promise.all([
        fetch(`${API}/v1/evidence/cross-framework-map?limit=50`, { headers }).catch(() => null),
        fetch(`${API}/v1/evidence/cross-framework-stats`, { headers }).catch(() => null),
        fetch(`${API}/v1/cfi/stats`, { headers }).catch(() => null),
      ]);
      
      if (mapRes?.ok) {
        const data = await mapRes.json();
        const transformedEvidence = (data.items || []).map(item => ({
          id: item.evidence_id,
          original_filename: item.filename,
          evidence_type: item.category,
          category: item.category,
          status: item.status,
          framework_mappings: (item.frameworks || []).map(fw => ({
            framework_code: fw,
            mapping_confidence: 0.8
          }))
        }));
        setEvidence(transformedEvidence);
      }
      
      if (statsRes?.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      } else if (cfiRes?.ok) {
        // Fallback: build stats from CFI intelligence data
        const cfiData = await cfiRes.json();
        setStats({
          totalEvidence: 0,
          totalMappings: cfiData.total_mapping_decisions || 0,
          averageFrameworksPerEvidence: cfiData.avg_frameworks_per_topic || 0,
          totalFrameworks: cfiData.total_frameworks || 14,
          byFramework: [],
        });
      }
    } catch (err) {
      console.error('Error fetching cross-framework data:', err);
      toast({
        title: 'Error',
        description: 'Failed to load cross-framework data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter evidence
  const filteredEvidence = evidence.filter(ev => {
    const filename = ev.original_filename || '';
    const matchesSearch = filename.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (selectedFramework === 'All') return matchesSearch;
    
    const mappings = ev.framework_mappings || [];
    const matchesFramework = mappings.some(m => m.framework_code === selectedFramework);
    
    return matchesSearch && matchesFramework;
  });

  const handleOpenDetail = (evidenceId) => {
    window.location.href = `/evidence?selected=${evidenceId}`;
  };

  return (
    <div className="space-y-6" data-testid="cross-framework-map-page">
      {/* Breadcrumb */}
      <div className="flex items-center justify-between">
        <div>
          <Link 
            to="/evidence" 
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-emerald-400 transition-colors mb-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Evidence Hub
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-white">Cross-Framework Intelligence Map</h1>
            <Badge className="bg-teal-500/20 text-teal-400 border-teal-500/30">
              <Zap size={12} className="mr-1" />
              Superpower #1
            </Badge>
          </div>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Visualize evidence reuse and maximize compliance efficiency
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="border-[#2A2A2A] text-gray-300"
            onClick={fetchData}
          >
            <RefreshCw size={14} className="mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" className="border-[#2A2A2A] text-gray-300">
            <Download size={14} className="mr-2" />
            Export Map
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <StatsBar stats={stats} loading={statsLoading} />

      {/* Top Reused Callout */}
      {stats?.topReusedEvidence && <TopReusedCallout topReused={stats.topReusedEvidence} />}

      {/* View Tabs */}
      <Tabs value={activeView} onValueChange={setActiveView} className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList className="bg-[#1A1A1A] border border-[#2A2A2A]">
            <TabsTrigger value="list" className="gap-2">
              <List size={14} />
              List View
            </TabsTrigger>
            <TabsTrigger value="matrix" className="gap-2">
              <Grid3X3 size={14} />
              Matrix View
            </TabsTrigger>
            <TabsTrigger value="frameworks" className="gap-2">
              <Network size={14} />
              By Framework
            </TabsTrigger>
          </TabsList>

          {/* Filters */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
              <input
                type="text"
                placeholder="Search evidence..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-emerald-500/50 focus:outline-none w-48"
                data-testid="mapping-search-input"
              />
            </div>
            
            <div className="flex items-center gap-1">
              <Filter size={14} className="text-[#6B6B6B]" />
              {FRAMEWORKS.map(fw => (
                <button
                  key={fw}
                  onClick={() => setSelectedFramework(fw)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    selectedFramework === fw 
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-[#1F1F1F] text-[#6B6B6B] hover:text-white'
                  }`}
                >
                  {fw}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* LIST VIEW */}
        <TabsContent value="list" className="space-y-4 m-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-emerald-400 animate-spin" />
            </div>
          ) : filteredEvidence.length > 0 ? (
            <div className="space-y-4">
              {filteredEvidence.map(ev => (
                <EvidenceMappingCard 
                  key={ev.id} 
                  evidence={ev}
                  onOpenDetail={handleOpenDetail}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-[#111111] border border-[#1F1F1F] rounded-xl">
              <Network size={48} className="mx-auto text-[#2F2F2F] mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No mappings found</h3>
              <p className="text-sm text-[#6B6B6B]">
                {searchQuery || selectedFramework !== 'All'
                  ? 'Try adjusting your search or filter criteria'
                  : 'Upload evidence and add framework mappings to get started'}
              </p>
            </div>
          )}
        </TabsContent>

        {/* MATRIX VIEW */}
        <TabsContent value="matrix" className="m-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-emerald-400 animate-spin" />
            </div>
          ) : (
            <MatrixView evidence={filteredEvidence} frameworks={FRAMEWORKS} />
          )}
        </TabsContent>

        {/* BY FRAMEWORK VIEW */}
        <TabsContent value="frameworks" className="m-0">
          <FrameworkBreakdown byFramework={stats?.byFramework} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
